<template>
  <div v-if="currentTeam || isDemo">
    <DefineNav v-slot="{ mobile }">
      <div
        class="flex min-h-0 grow flex-col gap-y-3 bg-white dark:bg-slate-950"
        :class="[mobile ? '' : 'border-r border-gray-200 pb-4 pt-2 dark:border-none']">
        <nav class="flex min-h-0 flex-1 flex-col">
          <div class="min-h-0 flex-1 overflow-y-auto overflow-x-hidden">
            <div class="space-y-3 px-6 pb-3 pt-2">
              <!-- Settings/Profile mode: Back to app -->
              <template v-if="isConfigMode">
                <router-link
                  :to="{
                    name: ROUTES.DASHBOARD,
                    params: { teamPid: route.params.teamPid || currentTeamPid || 'demo' }
                  }"
                  class="group flex items-center gap-x-2 rounded-md p-2 text-sm font-medium text-gray-500 transition-colors hover:bg-gray-50 hover:text-gray-900 dark:text-gray-400 dark:hover:bg-slate-800 dark:hover:text-white">
                  <BackwardIcon class="h-4 w-4" />
                  Back to app
                </router-link>
              </template>

              <!-- Main nav mode -->
              <template v-else>
                <!-- Agency management badge -->
                <div v-if="owner?.agency_owner?.name">
                  <a
                    :href="`mailto:${owner.agency_owner.email}?subject=RightMessage`"
                    class="block rounded-lg bg-sky-200/20 px-3 py-2 dark:bg-gray-800/50 dark:outline dark:outline-1 dark:-outline-offset-1 dark:outline-white/10">
                    <div class="text-xs text-gray-500 dark:text-gray-400">Managed by</div>
                    <div class="mt-1 flex items-center gap-2">
                      <img
                        v-if="owner.agency_owner.logo"
                        :src="owner.agency_owner.logo"
                        alt=""
                        class="h-5 w-5 rounded object-contain" />
                      <span class="text-sm font-medium text-gray-700 dark:text-white">
                        {{ owner.agency_owner.name }}
                      </span>
                    </div>
                  </a>
                </div>
              </template>
            </div>

            <ul role="list" class="space-y-0.5 px-4 pb-8">
              <li v-for="(item, index) in activeNavigation" :key="item.name">
                <div v-if="item.type == 'divider'">
                  <div
                    class="mb-1 ml-2 text-[11px] font-bold text-gray-400 dark:text-gray-500"
                    :class="index === 0 ? 'mt-1' : 'mt-4'">
                    {{ item.name }}
                  </div>
                </div>
                <!-- External link (e.g., Sign out) -->
                <a
                  v-else-if="item.href"
                  :href="item.href"
                  :class="[
                    'dark:hover:text-primary-200 text-gray-700 hover:bg-gray-50 hover:text-indigo-600 dark:text-white/80 dark:hover:bg-slate-700/50',
                    'group flex items-center gap-x-2.5 rounded-md px-2 py-1.5 text-sm font-medium leading-5'
                  ]">
                  <component
                    :is="item.icon"
                    class="h-4 w-4 shrink-0 text-gray-400 group-hover:text-indigo-600"
                    aria-hidden="true" />
                  {{ item.name }}
                </a>

                <!-- Router link (standard nav item) -->
                <router-link
                  v-else
                  :to="
                    item.comingsoon
                      ? {}
                      : {
                          ...item.to,
                          params: {
                            teamPid: route.params.teamPid || currentTeamPid || 'demo'
                          },
                          ...(item.query || {})
                        }
                  "
                  :class="[
                    isCurrentRoute(item)
                      ? 'text-primary-600 dark:text-primary-200 bg-gray-50 dark:bg-slate-700/50'
                      : 'dark:hover:text-primary-200 text-gray-700 hover:bg-gray-50 hover:text-indigo-600 dark:text-white/80 dark:hover:bg-slate-700/50',
                    isItemLocked(item) ? 'opacity-50' : '',
                    item.comingsoon ? 'cursor-not-allowed' : '',
                    'group flex items-center gap-x-2.5 rounded-md px-2 py-1.5 text-sm font-medium leading-5'
                  ]">
                  <component
                    :is="item.icon"
                    :class="[
                      isCurrentRoute(item) ? 'text-indigo-600' : 'text-gray-400 group-hover:text-indigo-600',
                      'h-4 w-4 shrink-0'
                    ]"
                    aria-hidden="true" />
                  <span class="flex items-center">
                    {{ item.name }}

                    <span v-if="item.aside" class="ml-2.5" v-html="item.aside" />
                  </span>
                  <Tag
                    v-if="isItemLocked(item)"
                    :value="
                      item.requiredFeature ? tierDisplayName(requiredTierForFeature(item.requiredFeature)) : 'Upgrade'
                    "
                    severity="success" />
                  <Tag v-else-if="item.comingsoon && !isDemo" value="Soon" severity="info" />
                  <Tag v-else-if="item.beta" value="Beta" severity="success" />
                </router-link>

                <div v-if="item.children?.length">
                  <ul role="list" class="ml-[1.125rem] border-l border-gray-200 pl-[15px] dark:border-slate-700">
                    <li v-for="child in item.children" :key="child.name">
                      <router-link
                        :to="{
                          ...child.to,
                          params: {
                            teamPid: route.params.teamPid || currentTeamPid || 'demo'
                          }
                        }"
                        :class="[
                          isCurrentRoute(child)
                            ? 'dark:text-primary-200 font-semibold text-indigo-600'
                            : 'dark:hover:text-primary-200 text-gray-500 hover:text-indigo-600 dark:text-white/60',
                          'block rounded-md px-0 py-1 text-sm leading-6'
                        ]">
                        {{ child.name }}
                      </router-link>
                    </li>
                  </ul>
                </div>
              </li>
            </ul>
          </div>

          <div v-if="!isConfigMode" class="relative z-10 min-w-0 space-y-3 px-6 pt-3">
            <!-- Gradient fade overlapping scrollable nav above -->
            <div
              class="pointer-events-none absolute -top-10 left-0 right-0 h-10 bg-gradient-to-t from-white to-transparent dark:from-slate-950" />
            <SidebarPromo />
            <div class="flex items-center gap-x-4 lg:gap-x-6">
              <router-link
                v-if="!isDemo && !isSettingsMode"
                :to="{ name: ROUTES.SETTINGS }"
                type="button"
                class="-m-2.5 p-2.5 text-gray-400 hover:text-gray-500">
                <SettingsIcon class="h-5 w-5" aria-hidden="true" />
              </router-link>
              <a href="https://rightmessage.com/help" target="_blank">
                <QuestionMarkCircleIcon class="h-5 w-5 text-gray-400 hover:text-gray-500" aria-hidden="true" />
              </a>
              <FeatureOs v-if="false" />
              <SunIcon
                v-if="!isDark"
                class="h-5 w-5 cursor-pointer text-gray-400 hover:text-gray-500"
                aria-hidden="true"
                @click="toggleDark()" />
              <MoonIcon
                v-else
                class="h-5 w-5 cursor-pointer text-gray-400 hover:text-gray-500"
                aria-hidden="true"
                @click="toggleDark()" />

              <!-- Separator -->
              <div class="hidden lg:block lg:h-5 lg:w-px lg:bg-gray-200" aria-hidden="true" />

              <!-- Profile link -->
              <router-link
                v-if="!isDemo"
                :to="{ name: ROUTES.PROFILE }"
                class="-m-1.5 flex items-center p-1.5"
                title="Your Profile">
                <img class="h-6 w-6 rounded-full bg-gray-50" :src="profilePhotoUrl" alt="Your Profile" />
              </router-link>
            </div>
          </div>
        </nav>
      </div>
    </DefineNav>
    <!-- mobile -->
    <div class="border-surface-400/50 border-b lg:hidden">
      <div class="flex items-center justify-between p-3">
        <HamburgerIcon class="h-6 w-6 shrink-0" @click="mobileNavVisible = true" />
        <PublishButton />
      </div>
      <Drawer v-model:visible="mobileNavVisible">
        <template #header>
          <div class="flex items-center gap-2">
            <router-link
              v-if="!isDemo"
              :to="{
                name: ROUTES.DASHBOARD,
                params: {
                  teamPid: route.params.teamPid
                }
              }">
              <img class="h-6 w-auto" src="https://t.rightmessage.com/logo.svg" alt="Back to the Dashboard" />
            </router-link>
            <TeamDropdown />
          </div>
        </template>

        <ReuseNav :mobile="true" />
      </Drawer>
    </div>

    <!-- desktop-->
    <div
      v-if="!route.meta.fullScreen"
      id="rm-sidebar"
      class="hidden lg:fixed lg:bottom-0 lg:top-12 lg:flex lg:w-64 lg:flex-col">
      <div
        v-if="isLocal && !isDemo"
        class="cursor-pointer bg-yellow-400 px-4 py-1 text-center text-xs transition-colors hover:bg-yellow-500 dark:bg-yellow-900 dark:hover:bg-yellow-800"
        title="Click to copy config to clipboard"
        @click="copyConfigToClipboard">
        Team #{{ currentTeam?.id }}
      </div>
      <ReuseNav :mobile="false" />
    </div>
  </div>
</template>

<script setup lang="ts">
import { createReusableTemplate } from '@vueuse/core'
import { storeToRefs } from 'pinia'
import {
  Cog6ToothIcon as SettingsIcon,
  QuestionMarkCircleIcon,
  SunIcon,
  MoonIcon,
  ArrowLeftIcon as BackwardIcon,
  Bars3Icon as HamburgerIcon
} from '@heroicons/vue/24/outline'
import { ROUTES } from '@/router/routeNames'
import { useSidebarNavigation } from '@/data/sidebarNavigation'
import { useSettingsNavigation, useProfileNavigation } from '@/data/settingsNavigation'
import { RouteLocation } from 'vue-router'

const [DefineNav, ReuseNav] = createReusableTemplate()

const route = useRoute()
const isLocal = import.meta.env.VITE_APP_ENV === 'development'

const isDark = useDark()
const toggleDark = useToggle(isDark)

const { canAccess: canAccessProxy } = usePermissionStore()
const { isFeatureLocked, requiredTierForFeature, tierDisplayName } = useFeatureGating()

const authStore = useAuthStore()
const { profilePhotoUrl } = storeToRefs(authStore)
const configStore = useConfigStore()
const { currentTeam, currentTeamPid, owner, config } = storeToRefs(configStore)
const notificationStore = useNotificationStore()
const teamStore = useTeamStore()
const { isDemo } = storeToRefs(teamStore)
const mobileNavVisible = ref(false)

const canAccess = (permission: string) => {
  if (isDemo.value) {
    return true
  }
  return canAccessProxy(permission)
}

const isItemLocked = (item: { permission?: string; requiredFeature?: string }) => {
  if (isDemo.value) return false
  if (item.permission && !canAccess(item.permission)) return true
  if (item.requiredFeature && isFeatureLocked(item.requiredFeature)) return true
  return false
}

const isSettingsMode = computed(() => !!route.meta?.settingsMode)
const isProfileMode = computed(() => !!route.meta?.profileMode)
const isConfigMode = computed(() => isSettingsMode.value || isProfileMode.value)
const navigation = useSidebarNavigation()
const settingsNavigation = useSettingsNavigation()
const profileNavigation = useProfileNavigation()
const activeNavigation = computed(() => {
  if (isSettingsMode.value) return settingsNavigation.value
  if (isProfileMode.value) return profileNavigation.value
  return navigation.value
})

const copyConfigToClipboard = async () => {
  try {
    if (!config.value) {
      notificationStore.addError('No config data available to copy')
      return
    }

    const configJson = JSON.stringify(config.value, null, 2)
    await navigator.clipboard.writeText(configJson)

    notificationStore.addNotification('Configuration copied to clipboard')
  } catch (error) {
    console.error('Failed to copy config:', error)
    notificationStore.addError('Failed to copy config to clipboard')
  }
}

watch(
  () => route.fullPath,
  () => {
    mobileNavVisible.value = false
  }
)

const isCurrentRoute = (item: { to: RouteLocation }) => {
  if (!item || !item.to || !item.to.name) return false

  if (route.name === item.to.name) return true

  const currentRouteName = route.name
  const itemRouteName = item.to.name
  const parentChildMap = {
    [ROUTES.SEGMENTGROUPS]: [ROUTES.SEGMENTGROUP],
    [ROUTES.QUESTIONS]: [ROUTES.QUESTION, ROUTES.SURVEY_RESPONSES],
    [ROUTES.INTEGRATIONS]: [ROUTES.INTEGRATION],
    [ROUTES.OFFERFUNNELS]: [ROUTES.OFFERFUNNEL, ROUTES.CREATEOFFERFUNNEL],
    [ROUTES.FORMS]: [ROUTES.FORM],
    [ROUTES.OFFERS]: [ROUTES.OFFER],
    [ROUTES.WIDGETS]: [ROUTES.WIDGET, ROUTES.CREATEWIDGET],
    [ROUTES.CAMPAIGNS]: [ROUTES.CREATECAMPAIGN, ROUTES.CAMPAIGN],
    [ROUTES.EMAILS]: [ROUTES.EMAIL],
    [ROUTES.GOALS]: [ROUTES.CREATEGOAL],
    [ROUTES.PEOPLE]: [ROUTES.VISITOR_DETAIL]
  }

  return parentChildMap[itemRouteName]?.includes(currentRouteName) || false
}
</script>
